import { Component, signal, computed, inject, ViewChild, ViewEncapsulation, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { OnboardingService } from '../../../core/api-services/onboarding.service';
import { OrgOnboardingItem } from '../../../core/response-models/org-onboarding-item.model';
import { PersonalOnboardingItem } from '../../../core/response-models/personal-onboarding-item.model';
import { OnboardingWizardModalComponent } from '../onboarding-wizard-modal/onboarding-wizard-modal.component';

/**
 * Unified Onboarding Component
 *
 * Compact card component that replaces both OnboardingChecklistComponent
 * and MemberOnboardingBannerComponent on the dashboard.
 *
 * Features:
 * - Compact by default, expandable to show full checklist
 * - Inline sections (Personal Setup / Organization Setup) for Owner/CS
 * - Single personal content for Member/Pilot/PIC
 * - Theme-aware: light card in light mode, dark card in dark mode
 * - Clickable personal items open wizard at the relevant step
 * - Optimistic dismiss/restore via service
 *
 * Reference: agent-os/specs/2026-02-13-unified-onboarding-redesign/spec.md
 */
@Component({
  selector: 'app-unified-onboarding',
  standalone: true,
  imports: [CommonModule, RouterModule, OnboardingWizardModalComponent],
  templateUrl: './unified-onboarding.component.html',
  styleUrls: ['./unified-onboarding.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class UnifiedOnboardingComponent implements OnDestroy {
  readonly onboardingService = inject(OnboardingService);
  private router = inject(Router);

  @ViewChild('onboardingWizardModal') onboardingWizardModal?: OnboardingWizardModalComponent;

  /** Whether the checklist detail is expanded */
  readonly isExpanded = signal(false);

  /** Whether the user is Owner or Chief Supervisor */
  readonly isOwnerOrCS = computed(() => this.onboardingService.isOwnerOrChiefSupervisor());

  /** Whether the celebration card has been shown/dismissed this session */
  readonly celebrationShown = signal(false);

  /** Whether onboarding was already complete when data first loaded (previous session) */
  readonly completedBeforeSession = signal(false);

  /** Whether the celebration card is currently displaying */
  readonly showCelebration = computed(() => {
    return this.onboardingService.isFullyComplete()
      && !this.completedBeforeSession()
      && !this.celebrationShown();
  });

  /** Whether the main onboarding card should be visible */
  readonly showCard = computed(() => {
    if (this.onboardingService.isLoading() && !this.onboardingService.hasLoadedOnce()) return false;
    if (!this.onboardingService.hasLoadedOnce()) return false;
    if (this.onboardingService.isDismissed()) return false;
    if (this.onboardingService.isFullyComplete()) return false;
    return true;
  });

  /** Whether any required personal items are still incomplete */
  readonly hasIncompleteRequiredPersonalItems = computed(() => {
    return this.onboardingService.personalItems()
      .some(item => item.isRequired && !item.isCompleted);
  });

  /** Whether the Continue Setup button should show (any required item incomplete) */
  readonly showContinueButton = computed(() => {
    if (this.hasIncompleteRequiredPersonalItems()) return true;
    if (this.isOwnerOrCS()) {
      return this.onboardingService.orgItems()
        .some(item => item.isRequired && !item.isCompleted);
    }
    return false;
  });

  /** Summary text for the compact view (e.g., "2 of 6 steps complete") */
  readonly completedSummary = computed(() => {
    const personal = this.onboardingService.personalItems();
    if (personal.length === 0) return '';

    const completedPersonal = personal.filter(i => i.isRequired && i.isCompleted).length;

    if (this.isOwnerOrCS()) {
      const completedOrg = this.onboardingService.orgItems()
        .filter(i => i.isRequired && i.isCompleted).length;
      const total = 6;
      return `${completedPersonal + completedOrg} of ${total} required steps complete`;
    }
    return `${completedPersonal} of 4 required steps complete`;
  });

  /** Title of the next incomplete required step (for compact view hint) */
  readonly nextIncompleteStep = computed<string | null>(() => {
    const personalItem = this.onboardingService.personalItems()
      .find(item => !item.isCompleted && item.isRequired);
    if (personalItem) return personalItem.title;

    if (this.isOwnerOrCS()) {
      const orgItem = this.onboardingService.orgItems()
        .find(item => !item.isCompleted && item.isRequired);
      if (orgItem) return orgItem.title;
    }
    return null;
  });

  /** Map personal item titles to wizard step numbers */
  private readonly personalStepMap: Record<string, number> = {
    'Complete Profile': 1,
    'Upload Part 107 Certificate': 2,
    'Upload FAA Medical Certificate': 3,
    'Upload Insurance Certificate': 4,
    'Upload State Applicator License': 5,
    'Review Safety Guidelines': 6
  };

  /** Auto-dismiss timeout reference for cleanup */
  private autoDismissTimeout: ReturnType<typeof setTimeout> | null = null;
  private hasCheckedInitialState = false;

  constructor() {
    // Capture whether onboarding was already complete on first data load.
    // If so, skip the celebration card (user completed in a previous session).
    effect(() => {
      if (this.onboardingService.hasLoadedOnce() && !this.hasCheckedInitialState) {
        this.hasCheckedInitialState = true;
        if (this.onboardingService.isFullyComplete()) {
          this.completedBeforeSession.set(true);
        }
      }
    });

    // Auto-dismiss celebration after 5 seconds
    effect((onCleanup) => {
      if (this.showCelebration()) {
        if (this.autoDismissTimeout) {
          clearTimeout(this.autoDismissTimeout);
        }
        this.autoDismissTimeout = setTimeout(() => {
          this.celebrationShown.set(true);
          this.onboardingService.dismiss();
        }, 5000);
        onCleanup(() => {
          if (this.autoDismissTimeout) {
            clearTimeout(this.autoDismissTimeout);
            this.autoDismissTimeout = null;
          }
        });
      }
    });
  }

  ngOnDestroy(): void {
    if (this.autoDismissTimeout) {
      clearTimeout(this.autoDismissTimeout);
    }
  }

  /** Toggle expand/collapse of the checklist detail */
  toggleExpand(): void {
    this.isExpanded.update(v => !v);
  }

  /** Smart dispatcher: opens wizard for personal steps, navigates for org steps */
  continueSetup(): void {
    if (this.hasIncompleteRequiredPersonalItems()) {
      this.onboardingWizardModal?.open();
      return;
    }

    // All required personal steps done — navigate to first incomplete org item
    const nextOrgItem = this.onboardingService.orgItems()
      .find(item => !item.isCompleted && item.isRequired && item.actionRoute);
    if (nextOrgItem?.actionRoute) {
      this.router.navigate([nextOrgItem.actionRoute]);
    }
  }

  /** Open the wizard at the step corresponding to a personal item */
  openWizardAtStep(item: PersonalOnboardingItem): void {
    if (item.isCompleted) return;
    const step = this.personalStepMap[item.title];
    this.onboardingWizardModal?.open(step);
  }

  onWizardCompleted(): void {
    this.onboardingService.refreshAfterWizardComplete();
  }

  onWizardCancelled(): void {
    // Refresh to reflect any partial progress saved before cancellation
    this.onboardingService.refreshAfterWizardComplete();
  }

  dismiss(): void {
    this.onboardingService.dismiss();
  }

  navigateToItem(item: OrgOnboardingItem): void {
    if (item.actionRoute && !item.isCompleted) {
      this.router.navigate([item.actionRoute]);
    }
  }
}
